import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { getCountries, getStates } from '../../api'
import { JsonState, State, JsonCountry, Country } from '../../models'

interface AdvancedSearchProps {
    setAdvancedSearch: (values: Record<string, any>) => void
}

export const AdvancedSearch: React.FC<AdvancedSearchProps> = ({ setAdvancedSearch }) => {
    const defaultState = {
        name: '',
        phone: '',
        fax: '',
        street: '',
        city: '',
        zip: '',
        state: '',
        country: '',
    }

    const [searchParams, setSearchParams] = useSearchParams()
    const advancedSearchKeys = useRef(new Set(Object.keys(defaultState)))

    const [localState, setLocalState] = useState(() => {
        const fromUrl: Record<string, any> = {}
        searchParams.forEach((value, key) => {
            if (advancedSearchKeys.current.has(key)) {
                fromUrl[key] = value
            }
        })
        return { ...defaultState, ...fromUrl }
    })

    const [states, setStates] = useState<State[]>([])
    const [countries, setCountries] = useState<Country[]>([])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setLocalState((prevState) => ({ ...prevState, [name]: value }))
    }

    type Timer = ReturnType<typeof setTimeout>
    const timeoutRef = useRef<Timer>()

    useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }

        timeoutRef.current = setTimeout(() => {
            setAdvancedSearch(localState)
            const params = new URLSearchParams(searchParams)
            for (const [key, value] of Object.entries(localState)) {
                if (!advancedSearchKeys.current.has(key)) continue

                if (value !== '' && value !== null && value !== undefined) {
                    params.set(key, String(value))
                } else {
                    params.delete(key)
                }
            }

            if (params.toString() !== searchParams.toString()) {
                setSearchParams(params, { replace: true })
            }
        }, 500)
    }, [JSON.stringify(localState)])

    useEffect(() => {
        getStates().then((json) =>
            setStates(json.data.map((state: JsonState) => new State(state)))
        )
        getCountries().then((json) =>
            setCountries(json.data.map((country: JsonCountry) => new Country(country)))
        )
    }, [])

    const handleClear = () => {
        window.location.href = window.location.pathname
    }

    return (
        <div>
            <div className="row g-2 mb-3 justify-content-end align-items-end">
                <div className="col-lg-3">
                    <input
                        className="form-control form-control-sm"
                        name="name"
                        value={localState.name || ''}
                        onChange={handleInputChange}
                        placeholder="Supplier Name"
                    />
                </div>
                <div className="col-lg-2">
                    <input
                        className="form-control form-control-sm"
                        name="phone"
                        value={localState.phone || ''}
                        onChange={handleInputChange}
                        placeholder="Phone #"
                    />
                </div>
                <div className="col-lg-2">
                    <input
                        className="form-control form-control-sm"
                        name="fax"
                        value={localState.fax || ''}
                        onChange={handleInputChange}
                        placeholder="Fax #"
                    />
                </div>
                <div className="col-lg-5">
                    <input
                        className="form-control form-control-sm"
                        name="street"
                        value={localState.street || ''}
                        onChange={handleInputChange}
                        placeholder="Street"
                    />
                </div>
            </div>

            <div className="row g-2 justify-content-end">
                <div className="col-lg-3">
                    <input
                        className="form-control form-control-sm"
                        name="city"
                        value={localState.city || ''}
                        onChange={handleInputChange}
                        placeholder="City"
                    />
                </div>
                <div className="col-lg-2">
                    <input
                        className="form-control form-control-sm"
                        name="zip"
                        value={localState.zip || ''}
                        onChange={handleInputChange}
                        placeholder="Zip Code"
                    />
                </div>
                <div className="col-lg-2">
                    <select
                        className="form-select form-select-sm"
                        name="state"
                        value={localState.state || ''}
                        onChange={handleInputChange}
                    >
                        <option value={''}>All States</option>
                        {states.map((state: State) => (
                            <option key={state.id} value={state.id}>
                                {state.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-lg-3">
                    <select
                        className="form-select form-select-sm"
                        name="country"
                        value={localState.country || ''}
                        onChange={handleInputChange}
                    >
                        <option value={''}>All Countries</option>
                        {countries.map((country: Country) => (
                            <option key={country.id} value={country.id}>
                                {country.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-lg-2">
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm w-100"
                        onClick={handleClear}
                    >
                        Clear All
                    </button>
                </div>
            </div>
        </div>
    )
}
